<template>
  <div class="relative">
    <div>
      <div
        class="mb-1 d-flex justify-content-between align-items-center flex-wrap mt-3"
      >
        <div class="d-flex flex-wrap align-items-center">
          <div>
            <b>Technician</b>
            <span v-if="!showAll" class="hint-text whitespace-nowrap"
              >(Only Qualified Technicians for Selected Service Category)</span
            >
          </div>
        </div>
        <Checkbox
          v-model="showAll"
          label="Include All Technicians"
          class="all-techs whitespace-nowrap mb-0"
        ></Checkbox>
      </div>
      <div v-if="warningMessage" class="warning-message whitespace-nowrap">
        <i class="fas fa-exclamation-triangle mr-1"></i>
        {{ warningMessage }}
      </div>
      <Select
        v-model="technicianId"
        placeholder="Auto Assign"
        :options="eligibleTechs"
        search
        full-width
        data-cy="technician-select"
      >
      </Select>
      <div v-if="capturePinnedReason" class="mt-2">
        <span class="label"
          >Reason for Switching <em>(Pinning)</em> Technician</span
        >
        <TextArea v-model="pinnedReasonText" data-cy="pinned-reason"></TextArea>
      </div>
      <div v-else-if="showPinnedReason" class="mt-2 d-flex">
        <span class="font-bold whitespace-nowrap">Pinned Reason:</span>
        <truncate-text :text="pinnedReason" class="ml-1"></truncate-text>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

import Checkbox from "@/components/Checkbox.vue";
import TextArea from "@/components/TextArea.vue";
import TruncateText from "@/components/TruncateText.vue";
import useVuelidate from "@vuelidate/core";

export default {
  components: {
    TextArea,
    TruncateText,
    Checkbox
  },
  props: {
    serviceCategoryId: {
      type: Number,
      default: null
    },
    modelValue: {
      type: Number,
      default: null
    },
    pinnedTechnicianId: {
      type: Number,
      default: null
    },
    pinnedReason: {
      type: String,
      default: "N/A"
    },
    noSave: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "update-pinned-reason"],
  data() {
    return {
      showAll: false,
      pinnedReasonText: this.pinnedReason
    };
  },
  computed: {
    ...mapState({
      eligibleTechs({ technicians }) {
        const techOptions = technicians.map((tech) => ({
          ...tech,
          label: this.getLabel(tech),
          value: tech.user_id
        }));
        if (!this.serviceCategoryId || this.showAll) {
          return techOptions;
        }
        return techOptions.filter(
          (tech) =>
            (tech.service_categories_ids || []).includes(
              this.serviceCategoryId
            ) || this.technicianId === tech.user_id
        );
      }
    }),
    technicianId: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit("update:modelValue", val);
      }
    },
    warningMessage() {
      const selectedTech = this.eligibleTechs.find(
        (tech) => tech.user_id === this.technicianId
      );
      if (
        selectedTech &&
        !selectedTech.service_categories_ids.includes(this.serviceCategoryId)
      ) {
        return "Technician Missing Service Category";
      }
      return "";
    },
    capturePinnedReason() {
      return (
        !this.noSave &&
        this.technicianId &&
        this.technicianId !== this.pinnedTechnicianId
      );
    },
    showPinnedReason() {
      return this.technicianId && this.technicianId === this.pinnedTechnicianId;
    }
  },
  watch: {
    technicianId(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (this.technicianId === this.pinnedTechnicianId) {
          // Reset pinned reason
          this.pinnedReasonText = this.pinnedReason;
        } else {
          // Clear pinned reason when changing techs
          this.pinnedReasonText = "";
        }
      }
    },
    pinnedReasonText(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$emit("update-pinned-reason", newValue);
      }
    }
  },
  mounted() {
    this.fetchTechnicians();
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  methods: {
    ...mapActions(["fetchTechnicians"]),
    getLabel(technician) {
      let label = this.fullName(technician);
      if (this.isPinned(technician)) {
        label += ` (Pinned)`;
      }
      return label;
    },
    isPinned(technician) {
      return technician.user_id === this.pinnedTechnicianId;
    }
  }
};
</script>

<style lang="scss" scoped>
.all-techs {
  font-size: 0.9em;
}
.warning-message {
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--warning-yellow);
  padding: 5px;
  margin-bottom: 0.5rem;
  text-align: center;
}
</style>
